import React from 'react';
import { CheckCircle2, AlertCircle, Phone, Send, ArrowRight, Loader2 } from 'lucide-react';

/**
 * Panel pubblici per il flusso firma: Loading / Error / Success / OTP.
 * Layout brand Flusia: surface #FBFBFA, card 18px radius, gradient arancio→magenta sui CTA primari.
 */

const PageBg: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="min-h-screen flex flex-col items-center justify-center px-4 py-8 sm:py-12"
    style={{ background: '#FBFBFA' }}
  >
    {children}
  </div>
);

const FlusiaBrand: React.FC<{ subtitle?: string }> = ({ subtitle }) => (
  <div className="mb-6 text-center">
    <div
      className="inline-block text-lg font-semibold tracking-tight bg-clip-text text-transparent"
      style={{ backgroundImage: 'linear-gradient(135deg, #F57C2A 0%, #D6336C 100%)' }}
    >
      Flusia
    </div>
    {subtitle && <div className="text-xs text-[#9D9892] mt-0.5">{subtitle}</div>}
  </div>
);

const PrimaryButton: React.FC<{
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, loading, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled || loading}
    className="w-full inline-flex justify-center items-center gap-2 px-5 py-3 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
    style={{
      background: disabled || loading ? '#9D9892' : 'linear-gradient(135deg, #F57C2A 0%, #D6336C 100%)',
    }}
  >
    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
    {children}
  </button>
);

export const LoadingPanel: React.FC<{ message?: string }> = ({ message = 'Caricamento in corso…' }) => (
  <PageBg>
    <FlusiaBrand />
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm px-8 py-10 max-w-sm w-full flex flex-col items-center">
      <Loader2 className="h-8 w-8 animate-spin text-[#D6336C]" />
      <p className="mt-4 text-sm text-[#6B6762] text-center">{message}</p>
    </div>
  </PageBg>
);

export const ErrorPanel: React.FC<{ message: string; onClose?: () => void }> = ({ message, onClose }) => (
  <PageBg>
    <FlusiaBrand subtitle="Firma documenti" />
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm p-8 max-w-md w-full">
      <div className="flex flex-col items-center text-center">
        <div className="flex items-center justify-center h-12 w-12 rounded-full bg-[#C9395A]/[0.11]">
          <AlertCircle className="h-6 w-6 text-[#C9395A]" />
        </div>
        <h3 className="mt-4 text-lg font-semibold text-[#37352F]">Si è verificato un errore</h3>
        <p className="mt-2 text-sm text-[#6B6762]">{message}</p>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="mt-6 px-5 py-2.5 text-sm font-medium text-[#37352F] bg-black/[0.045] hover:bg-black/[0.07] rounded-xl transition-colors"
          >
            Chiudi
          </button>
        )}
      </div>
    </div>
  </PageBg>
);

export const SuccessPanel: React.FC<{
  signerName?: string;
  pdfUrl?: string | null;
}> = ({ signerName, pdfUrl }) => (
  <PageBg>
    <FlusiaBrand subtitle="Firma documenti" />
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm p-8 max-w-md w-full text-center">
      <div className="flex items-center justify-center h-14 w-14 mx-auto rounded-full bg-[#1F8A5B]/[0.12]">
        <CheckCircle2 className="h-7 w-7 text-[#1F8A5B]" />
      </div>
      <h3 className="mt-5 text-xl font-bold text-[#37352F]">Documento firmato</h3>
      <p className="mt-2 text-sm text-[#6B6762] leading-relaxed">
        {signerName ? `Grazie ${signerName}.` : 'Grazie.'} La tua firma è stata registrata e il
        contratto è ora attivo. Riceverai una copia via email.
      </p>
      {pdfUrl && (
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          download
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-[#37352F] bg-black/[0.045] hover:bg-black/[0.07] rounded-xl transition-colors"
        >
          Scarica documento firmato
        </a>
      )}
    </div>
  </PageBg>
);

export const OtpPanel: React.FC<{
  phone: string;
  onPhoneChange: (v: string) => void;
  otpCode: string;
  onOtpChange: (v: string) => void;
  otpSent: boolean;
  loading?: boolean;
  onSendOtp: () => void;
  onVerifyOtp: () => void;
  onChangeNumber: () => void;
}> = ({ phone, onPhoneChange, otpCode, onOtpChange, otpSent, loading, onSendOtp, onVerifyOtp, onChangeNumber }) => (
  <PageBg>
    <FlusiaBrand subtitle="Verifica identità" />
    <div className="bg-white rounded-2xl border border-black/[0.08] shadow-sm p-8 max-w-md w-full">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center h-12 w-12 mx-auto rounded-full bg-[#F57C2A]/[0.12]">
          <Phone className="h-6 w-6 text-[#F57C2A]" />
        </div>
        <h2 className="mt-4 text-lg font-semibold text-[#37352F]">
          {otpSent ? 'Inserisci il codice' : 'Verifica il tuo numero'}
        </h2>
        <p className="mt-1.5 text-sm text-[#6B6762]">
          {otpSent
            ? `Ti abbiamo inviato un SMS al ${phone}`
            : 'Per la sicurezza ti chiediamo di confermare con un SMS'}
        </p>
      </div>

      {!otpSent ? (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#6B6762] uppercase tracking-wide mb-1.5">
              Numero di telefono
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder="+39 333 1234567"
              className="block w-full px-4 py-3 text-base text-[#37352F] bg-black/[0.03] border border-black/[0.09] rounded-xl focus:ring-2 focus:ring-[#D6336C]/30 focus:border-[#D6336C]/40 focus:outline-none transition-shadow placeholder-[#9D9892]"
            />
          </div>
          <PrimaryButton onClick={onSendOtp} loading={loading} disabled={!phone}>
            <Send className="h-4 w-4" />
            Invia codice via SMS
          </PrimaryButton>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#6B6762] uppercase tracking-wide mb-1.5">
              Codice a 6 cifre
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={otpCode}
              onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="• • • • • •"
              maxLength={6}
              className="block w-full px-4 py-3 text-xl tracking-[0.4em] text-center font-medium text-[#37352F] bg-black/[0.03] border border-black/[0.09] rounded-xl focus:ring-2 focus:ring-[#D6336C]/30 focus:border-[#D6336C]/40 focus:outline-none transition-shadow placeholder-[#9D9892]"
            />
          </div>
          <PrimaryButton onClick={onVerifyOtp} loading={loading} disabled={otpCode.length < 6}>
            <ArrowRight className="h-4 w-4" />
            Verifica e continua
          </PrimaryButton>
          <div className="flex justify-between text-xs">
            <button
              type="button"
              onClick={onChangeNumber}
              className="text-[#6B6762] hover:text-[#37352F] transition-colors"
            >
              ← Cambia numero
            </button>
            <button
              type="button"
              onClick={onSendOtp}
              className="text-[#D6336C] hover:text-[#F57C2A] transition-colors font-medium"
            >
              Reinvia codice
            </button>
          </div>
        </div>
      )}
    </div>
  </PageBg>
);
